import {
  LineChart,
  Line,
  Rectangle,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LabelList,
  ResponsiveContainer
} from "recharts";
import { useState, useEffect } from "react";
import "./Charts.css";
import { handleClickLogic } from "../../utils/filterFunctions";
import CustomBarLabel from "./CustomBarLabel";

const LineChartComparison = ({ showData, xKey, title, selectedKey, frozenKey, onBarClick }) => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isFrozen, setIsFrozen] = useState(false);
  const [frozenData, setFrozenData] = useState(null);

  const dataToRender = isFrozen ? frozenData : showData;

  useEffect(() => {
    setSelectedCategory(selectedKey);
  }, [selectedKey]);

  useEffect(() => {
    if (frozenKey !== title) {
      setIsFrozen(false);
      setFrozenData(null);
    }
  }, [frozenKey, title]);

  const hasData = Array.isArray(dataToRender) && dataToRender.length > 0;

  if (!hasData) {
    return (
      <div className="chart-card">
        <h3>{title}</h3>
        <div className="chart-body empty">
          <p style={{ textAlign: "center", color: "#888", margin: "20px 0" }}>
            Sin datos disponibles
          </p>
        </div>
      </div>
    );
  }

  const colors = ['#00BFFF', '#FFA500'];
  const barKeys = Object.keys(dataToRender[0]).filter(key => key !== xKey);

  const CustomTick = ({ x, y, payload, total }) => {
    const label = payload.value || "";
    const length = label.length;

    const getBaseFontSize = () => {
      if (total > 35) return 7;
      if (total > 25) return 8;
      if (total > 15) return 9;
      if (total > 10) return 10;
      return 11;
    };

    const adjustForLength = (base) => {
      if (length > 40) return base - 3;
      if (length > 25) return base - 2;
      if (length > 15) return base - 1;
      return base;
    };

    const baseFont = getBaseFontSize();
    const finalFont = Math.max(adjustForLength(baseFont), 6);

    const shouldRotate = total > 10;

    const adjustedY = y + (shouldRotate ? 30 : 2);

    return (
      <foreignObject
        x={x - (shouldRotate ? 40 : 50)}
        y={adjustedY}
        width={shouldRotate ? 60 : 80}
        height={shouldRotate ? 60 : 45}
        style={{ overflow: "visible" }}
      >
        <div
          style={{
            fontSize: `${finalFont}px`,
            textAlign: "center",
            lineHeight: "1.1",
            wordWrap: "break-word",
            overflowWrap: "break-word",
            whiteSpace: "normal",
            transform: shouldRotate ? "rotate(-40deg)" : "none",
            transformOrigin: "top left",
          }}
        >
          {label}
        </div>
      </foreignObject>
    );
  };

  return (
    <div className="chart-card">
      <h3>{title}</h3>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={dataToRender}
          onClick={(e) => {
            if (e && e.activeLabel) {
              handleClickLogic({
                data: { payload: { [xKey]: e.activeLabel } },
                xKey,
                selectedCategory,
                isFrozen,
                showData,
                setIsFrozen,
                setFrozenData,
                setSelectedCategory,
                onBarClick,
              });
            }
          }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey={xKey}
            interval={0}
            tick={(props) => (
              <CustomTick {...props} total={dataToRender.length} />
            )}
          />
          <YAxis />
          <Tooltip />
          <Legend />

          {barKeys.map((key, index) => (
            <Line
              key={key}
              type="monotone"
              dataKey={key}
              stroke={colors[index % colors.length]}
              strokeWidth={2}
              dot={(dotProps) => {
                const { cx, cy, payload } = dotProps;
                const category = payload[xKey];
                const isSelected = selectedCategory === category;
                const isDimmed =
                  isFrozen && selectedCategory && !isSelected ? 0.3 : 1;

                return (
                  <circle
                    cx={cx}
                    cy={cy}
                    r={5}
                    stroke={colors[index % colors.length]}
                    strokeWidth={1.5}
                    fill={isSelected ? colors[index % colors.length] : "white"}
                    opacity={isDimmed}
                    cursor="pointer"
                  />
                );
              }}
              activeDot={{ r: 6 }}
            >
              <LabelList dataKey={key} content={(props) => <CustomBarLabel {...props} chartType='line' lineIndex={index} />} />
            </Line>
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export default LineChartComparison;